import RootStore from '../../../../store/RootStore'
import ApplicationStore from '../ApplicationStore'
import { StyleSheet, Text, View } from 'react-native'
import { BtButton, BtCheckBox, BtText } from '@linqpal/components/src/ui'
import { Trans, useTranslation } from 'react-i18next'
import { commonColors } from '@linqpal/common-frontend/src/theme'
import { composeStyle } from '@linqpal/components/src/helpers'
import { Col, Row } from '../../../../ui/atoms/Grid'
import React, { CSSProperties } from 'react'
import { SubmitButton } from '../components'
import { useResponsive } from '../../../../utils/hooks'
import { observer } from 'mobx-react'
import { urls } from '@linqpal/models/src/dictionaries'

interface ComplianceProps {
  canSubmit: boolean
  document: any
  style?: CSSProperties
}

export const ConsentStatement = observer(
  ({ canSubmit, document, style }: ComplianceProps) => {
    const { t } = useTranslation('application')
    const { sm } = useResponsive()

    const {
      isGetPaidApplication,
      buttonLoading,
      hasSubmissionRights,
      isCreditApplication,
      agreementsAccepted,
    } = ApplicationStore

    const flowStep = RootStore.userStore?.document?.current
    const isPreview = flowStep === 'review.preview'

    return (
      <View style={composeStyle(styles.container, style)}>
        {isPreview && hasSubmissionRights ? (
          <Row>
            <Col xs={sm ? 8 : 12}>
              <ConsentStatementText />
            </Col>
            {sm && (
              <Col xs={3}>
                {isGetPaidApplication || isCreditApplication ? (
                  <BtButton
                    disabled={
                      !canSubmit ||
                      buttonLoading ||
                      !agreementsAccepted ||
                      RootStore.isBusy
                    }
                    style={{
                      width: 320,
                      marginBottom: 36,
                    }}
                    onPress={() => document.setSection('review.agreement')}
                    testID="ApplicationContinueToAgreementButton"
                    loading={buttonLoading}
                  >
                    {t('Review.Continue')}
                  </BtButton>
                ) : (
                  <SubmitButton t={t} canSubmit={canSubmit} />
                )}
              </Col>
            )}
          </Row>
        ) : (
          <ConsentStatementText />
        )}
      </View>
    )
  },
)

const ConsentStatementText = observer(() => {
  const { t } = useTranslation('application')

  const flowStep = RootStore.userStore?.document?.current

  const Statement = () => {
    if (flowStep?.startsWith('review')) {
      if (ApplicationStore.isCreditApplication) {
        return <CreditApplicationStatement />
      } else if (ApplicationStore.isInHouseCreditApplication) {
        return <InHouseCreditStatement />
      } else {
        return <SupplierApplicationStatement />
      }
    } else {
      return (
        <BtText style={styles.agreement}>{t('ToComplyWithFederalLaws')}</BtText>
      )
    }
  }

  return (
    <View style={{ flexDirection: 'row' }}>
      {ApplicationStore.hasSubmissionRights ? (
        <BtCheckBox
          style={styles.acceptAgreementsCheckbox}
          checked={ApplicationStore.agreementsAccepted}
          onPress={ApplicationStore.setAgreementsAccepted}
        />
      ) : null}
      <Statement />
    </View>
  )
})

const CreditApplicationStatement = observer(() => {
  const { t } = useTranslation('application')
  const { onClickLink, previewMasterAgreement } = ApplicationStore

  const onClickPgaLink = () =>
    ApplicationStore.previewPersonalGuarantorAgreement()

  return (
    <BtText style={styles.creditAgreement}>
      <Trans
        t={t}
        i18nKey="TermsAndConditionsMessage"
        components={{
          terms: (
            <Text style={styles.link} onPress={() => onClickLink(urls.TC)} />
          ),
          privacy: (
            <Text style={styles.link} onPress={() => onClickLink(urls.PP)} />
          ),
          PGA: (
            <Text
              style={composeStyle(styles.link, {
                textDecorationLine: 'underline',
              })}
              onPress={onClickPgaLink}
            />
          ),
          TCA: (
            <Text
              style={composeStyle(styles.link, {
                textDecorationLine: 'underline',
              })}
              onPress={previewMasterAgreement}
            />
          ),
        }}
      />
    </BtText>
  )
})

const InHouseCreditStatement = observer(() => {
  const { t } = useTranslation('application')

  return (
    <BtText style={styles.agreement}>
      <Trans
        t={t}
        i18nKey={'InHouseCreditConsentStatement'}
        components={{
          terms: (
            <Text
              style={styles.link}
              onPress={() => ApplicationStore.onClickLink(urls.TC)}
            />
          ),
          privacy: (
            <Text
              style={styles.link}
              onPress={() => ApplicationStore.onClickLink(urls.PP)}
            />
          ),
        }}
      />
    </BtText>
  )
})

const SupplierApplicationStatement = observer(() => {
  const { t } = useTranslation('application')
  const {
    previewInvoicePurchaseAgreement,
    previewSellerAgreement,
    previewGuarantySecuredAgreement,
  } = ApplicationStore

  return (
    <BtText style={styles.agreement}>
      <Trans
        t={t}
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        i18nKey="BySubmittingYourApplication"
        components={{
          BCA: (
            <Text
              style={{
                color: commonColors.accentText,
                textDecorationLine: 'underline',
              }}
              onPress={previewSellerAgreement}
            />
          ),
          GSA: (
            <Text
              style={{
                color: commonColors.accentText,
                textDecorationLine: 'underline',
              }}
              onPress={previewGuarantySecuredAgreement}
            />
          ),
          IPA: (
            <Text
              style={{
                color: commonColors.accentText,
                textDecorationLine: 'underline',
              }}
              onPress={previewInvoicePurchaseAgreement}
            />
          ),
        }}
      />
    </BtText>
  )
})

const styles = StyleSheet.create({
  container: {
    width: '100%',
    backgroundColor: '#F5F7F8',
    padding: 20,
    alignItems: 'center',
    alignSelf: 'baseline',
  },
  agreement: {
    maxWidth: 615,
    textAlign: 'justify',
    color: '#668598',
    lineHeight: 21,
  },
  preview: {
    maxWidth: 720,
    textAlign: 'center',
    color: '#668598',
    lineHeight: 21,
  },
  creditAgreement: {
    maxWidth: 720,
    textAlign: 'center',
    color: '#668598',
    fontSize: 12,
    fontWeight: '400',
    lineHeight: 14,
  },
  link: {
    color: '#00A0F3',
  },
  acceptAgreementsCheckbox: {
    width: 20,
    height: 20,
    marginTop: 2,
    marginRight: 10,
  },
})
